import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, X, Send } from "lucide-react";

export default function PublicRSVP() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState("invitation");
  const [currentStep, setCurrentStep] = useState(1); // invitation, rsvp, gifts
  const [form, setForm] = useState({ status: "confirmed", plus_one_names: [], plus_one_responses: [], dietary: "", message: "" });
  const [gifts, setGifts] = useState([]);
  
  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const messagesEndRef = useRef(null);

  const [pin, setPin] = useState("");
  const [needsPin, setNeedsPin] = useState(false);
  const [pinError, setPinError] = useState("");

  const pinConfig = useMemo(
    () => (pin ? { headers: { "X-RSVP-PIN": pin } } : undefined),
    [pin],
  );

  const loadChat = useCallback(() => {
    const request = pinConfig ? api.get(`/rsvp/${token}/chat`, pinConfig) : api.get(`/rsvp/${token}/chat`);
    request.then(r => setChatMessages(r.data)).catch(() => {});
  }, [pinConfig, token]);

  const loadData = useCallback((currentPin = "") => {
    const config = currentPin ? { headers: { "X-RSVP-PIN": currentPin } } : undefined;
    const request = config ? api.get(`/rsvp/${token}`, config) : api.get(`/rsvp/${token}`);
    request
      .then((r) => {
        if (r.data.require_pin) {
          setNeedsPin(true);
          if (currentPin) setPinError("Code PIN incorrect");
        } else {
          setNeedsPin(false);
          setPinError("");
          setData(r.data);
          const initNames = r.data.guest.plus_one_names && r.data.guest.plus_one_names.length > 0 
            ? r.data.guest.plus_one_names 
            : Array(r.data.guest.plus_ones_count || 0).fill("");
          const initPlusOneResponses = (r.data.guest.plus_one_responses || []).length > 0
            ? r.data.guest.plus_one_responses
            : initNames.map((name) => ({ name, status: "pending" }));
          const members = r.data.household_members || [];
          const hhMembers = members.map(m => ({ guest_id: m.id, status: m.rsvp_status !== "pending" ? m.rsvp_status : "confirmed", name: m.name }));
          
          if (r.data.guest.rsvp_status !== "pending") {
            setForm({ status: r.data.guest.rsvp_status, plus_one_names: initNames, plus_one_responses: initPlusOneResponses, dietary: r.data.guest.dietary || "", message: "", household_members: hhMembers });
          } else {
            setForm(f => ({ ...f, status: "confirmed", plus_one_names: initNames, plus_one_responses: initPlusOneResponses, household_members: hhMembers }));
          }
          api.get(`/public/gifts/${token}`).then((res) => setGifts(res.data)).catch(() => {});
          loadChat();
        }
      })
      .catch(() => setError("Invitation introuvable"));
  }, [loadChat, token]);

  useEffect(() => {
    loadData();

    // Check if redirecting back from a successful Stripe checkout session
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      toast.success("✨ Merci infiniment pour votre contribution ! 💛");
      setView("gifts"); // Go to gifts tab
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [loadData, token]);

  useEffect(() => {
    if (chatOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      const request = pinConfig ? api.post(`/rsvp/${token}/chat/read`, {}, pinConfig) : api.post(`/rsvp/${token}/chat/read`, {});
      request.catch(() => {});
    }
  }, [chatOpen, chatMessages, pinConfig, token]);

  const handleSendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    try {
      const res = pinConfig
        ? await api.post(`/rsvp/${token}/chat`, { content: chatInput.trim() }, pinConfig)
        : await api.post(`/rsvp/${token}/chat`, { content: chatInput.trim() });
      setChatMessages(prev => [...prev, res.data.message]);
      setChatInput("");
    } catch (e) {
      toast.error("Erreur d'envoi");
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (pinConfig) {
        await api.post(`/rsvp/${token}`, form, pinConfig);
      } else {
        await api.post(`/rsvp/${token}`, form);
      }
      toast.success("Merci pour votre réponse !");
      setData((d) => ({ ...d, guest: { ...d.guest, rsvp_status: form.status } }));
      if (form.status === "declined") {
        setView("success_declined");
      } else {
        // Optionnel : rediriger vers cadeaux si présent
      }
    } catch (e) { toast.error("Erreur"); }
    finally { setSubmitting(false); }
  };

  const claimGift = async (giftId, type) => {
    const guestName = data.guest.name;
    try {
      if (type === "cash") {
        const amt = parseFloat(prompt("Montant (€) ?", "50") || "0");
        if (!amt || isNaN(amt)) return;
        
        // Initiate Stripe Checkout session (or mock payment success url)
        const response = await api.post("/stripe/checkout", {
          gift_id: giftId,
          amount: amt,
          guest_name: guestName,
          rsvp_token: token
        });
        
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
          return;
        }
      } else {
        await api.post(`/public/gifts/${token}/${giftId}/claim`, { guest_name: guestName });
      }
      toast.success("Merci !");
      api.get(`/public/gifts/${token}`).then((r) => setGifts(r.data));
    } catch (e) {
      toast.error("Erreur");
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center bg-ivory"><div className="font-serif text-2xl">{error}</div></div>;
  
  if (needsPin) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center p-6">
        <div className="bg-white p-10 rounded-2xl shadow-xl border border-stone-200 max-w-md w-full text-center">
          <div className="text-4xl mb-6">🔑</div>
          <h2 className="font-serif text-3xl text-charcoal mb-2">Code Personnel</h2>
          <p className="text-sm text-stone-500 font-light mb-8">Veuillez entrer le code à 4 chiffres présent sur votre faire-part.</p>
          <form onSubmit={(e) => { e.preventDefault(); loadData(pin); }} className="space-y-4">
            <input 
              type="text" 
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Code PIN" 
              className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-sm text-center font-mono text-2xl tracking-[0.5em] focus:outline-none focus:border-sage"
            />
            {pinError && <div className="text-red-500 text-xs">{pinError}</div>}
            <button type="submit" className="w-full bg-charcoal text-white py-3 rounded-sm font-label-caps text-xs tracking-widest uppercase hover:bg-stone-800 transition-colors">
              Accéder à l'invitation
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-ivory"><div className="font-serif text-2xl animate-pulse">Chargement…</div></div>;

  const { guest, wedding, invitation } = data;
  const inv = invitation || { background_color: "#fcf9f8", text_color: "#1b1c1c", accent_color: "#d1948b", heading_text: "Save the Date", body_text: "", date_text: "", venue_text: "" };

  return (
    <div className="min-h-screen" style={{ background: inv.background_color, color: inv.text_color }}>
      {/* Top nav */}
      <nav className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: inv.accent_color + "30" }}>
        <div className="font-serif text-xl">{wedding.bride_name} & {wedding.groom_name}</div>
        <div className="flex gap-4 text-xs uppercase tracking-widest">
          <button onClick={() => setView("invitation")} className={view === "invitation" ? "underline" : ""} data-testid="tab-invitation">Invitation</button>
          <button onClick={() => setView("rsvp")} className={view === "rsvp" ? "underline" : ""} data-testid="tab-rsvp">RSVP</button>
          <button onClick={() => setView("gifts")} className={view === "gifts" ? "underline" : ""} data-testid="tab-gifts">Cadeaux</button>
        </div>
      </nav>

      {view === "invitation" && (
        <div className="max-w-2xl mx-auto p-8 lg:p-16 text-center cinematic-fade" key="invitation" data-testid="public-invitation">
          <div className="overline mb-4" style={{ color: inv.accent_color }}>Cher {guest.name}</div>
          <div className="font-serif text-5xl lg:text-7xl leading-tight mb-6">{inv.heading_text}</div>
          <div className="my-8 mx-auto w-16 h-px" style={{ background: inv.accent_color }} />
          <p className="text-base lg:text-lg leading-relaxed whitespace-pre-wrap">{inv.body_text}</p>
          {inv.date_text && <div className="font-serif text-3xl lg:text-4xl mt-12" style={{ color: inv.accent_color }}>{inv.date_text}</div>}
          {(wedding.venue || inv.venue_text) && <div className="text-sm mt-2 opacity-80">{inv.venue_text || wedding.venue}</div>}
          {wedding.story && (
            <div className="mt-16 text-left">
              <div className="overline mb-2" style={{ color: inv.accent_color }}>Notre histoire</div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{wedding.story}</p>
            </div>
          )}
          <button onClick={() => setView("rsvp")} className="mt-12 px-8 py-3 text-sm" style={{ background: inv.accent_color, color: inv.background_color }} data-testid="goto-rsvp">Répondre maintenant</button>
        </div>
      )}

      {view === "rsvp" && (
        <div className="w-full flex flex-col items-center max-w-5xl mx-auto py-12 px-4 relative z-10" key="rsvp" data-testid="public-rsvp-form">
          {currentStep === 1 && (
            <div className="w-full flex flex-col items-center animate-fade-in-up">
              <div className="text-center mb-16">
                <span className="font-label-caps text-[11px] uppercase tracking-[0.5em] mb-6 block" style={{color: inv.accent_color}}>Étape 1 sur 4</span>
                <h1 className="font-serif text-5xl md:text-7xl mb-6 text-on-surface">Votre Présence</h1>
                <p className="font-body-lg text-on-surface-variant italic max-w-xl mx-auto leading-relaxed opacity-70">
                  Nous vous prions de bien vouloir confirmer votre présence.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 w-full max-w-4xl mb-16">
                <button 
                  onClick={() => { setForm({ ...form, status: "confirmed" }); setTimeout(() => setCurrentStep(2), 400); }} 
                  className={`selection-tile group relative flex flex-col items-center justify-center aspect-[4/5] md:aspect-square border rounded-2xl p-12 overflow-hidden bg-white/40 backdrop-blur-md ${form.status === "confirmed" ? "active border-primary" : "border-outline-variant/20"}`}
                >
                  <div className="absolute inset-0 bg-primary/[0.01] group-hover:bg-primary/[0.03] transition-colors duration-700"></div>
                  <div className="relative z-10 flex flex-col items-center">
                    <span className="material-symbols-outlined icon-scale text-[64px] mb-10 transition-all duration-700 font-light" style={{color: form.status === "confirmed" ? inv.accent_color : "inherit"}}>auto_awesome</span>
                    <h3 className="font-serif text-[32px] mb-6 text-on-surface">Avec Joie</h3>
                    <p className="font-body-md text-on-surface-variant/60 text-center text-sm tracking-[0.1em] uppercase">Je serai présent pour célébrer</p>
                  </div>
                </button>
                <button 
                  onClick={() => { setForm({ ...form, status: "declined" }); setTimeout(() => setCurrentStep(2), 400); }} 
                  className={`selection-tile group relative flex flex-col items-center justify-center aspect-[4/5] md:aspect-square border rounded-2xl p-12 overflow-hidden bg-white/40 backdrop-blur-md ${form.status === "declined" ? "active border-primary" : "border-outline-variant/20"}`}
                >
                  <div className="absolute inset-0 bg-secondary/[0.01] group-hover:bg-secondary/[0.03] transition-colors duration-700"></div>
                  <div className="relative z-10 flex flex-col items-center">
                    <span className="material-symbols-outlined icon-scale text-[64px] mb-10 transition-all duration-700 font-light" style={{color: form.status === "declined" ? inv.accent_color : "inherit"}}>favorite</span>
                    <h3 className="font-serif text-[32px] mb-6 text-on-surface">Avec Regret</h3>
                    <p className="font-body-md text-on-surface-variant/60 text-center text-sm tracking-[0.1em] uppercase">Je ne pourrai malheureusement pas être là</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="w-full max-w-xl mx-auto flex flex-col items-center animate-fade-in-up">
              <div className="step-progress-dots mb-8">
                <div className="step-dot active"></div>
                <div className="step-dot active" style={{backgroundColor: inv.accent_color}}></div>
                <div className="step-dot"></div>
                <div className="step-dot"></div>
              </div>
              <div className="text-center mb-16">
                <span className="font-label-caps text-[11px] uppercase tracking-[0.5em] mb-4 block" style={{color: inv.accent_color}}>Étape 2 sur 4</span>
                <h2 className="font-serif text-4xl text-on-surface">Vos Coordonnées</h2>
              </div>
              <div className="w-full space-y-12">
                <div className="floating-label-group">
                  <input type="text" id="fullname" value={guest.name} readOnly className="w-full input-editorial font-body-lg text-on-surface bg-transparent" placeholder=" " />
                  <label htmlFor="fullname" className="floating-label-editorial">Nom Complet</label>
                </div>
                {guest.email && (
                  <div className="floating-label-group">
                    <input type="email" id="email" value={guest.email || ""} readOnly className="w-full input-editorial font-body-lg text-on-surface bg-transparent" placeholder=" " />
                    <label htmlFor="email" className="floating-label-editorial">Adresse Email</label>
                  </div>
                )}
                <div className="flex items-center justify-between pt-12">
                  <button onClick={() => setCurrentStep(1)} className="flex items-center gap-2 font-label-caps text-xs tracking-widest text-on-surface-variant hover:text-primary uppercase transition-all btn-text-luxury">
                    <span className="material-symbols-outlined text-sm">west</span> Retour
                  </button>
                  {form.status === "declined" ? (
                    <button onClick={submit} disabled={submitting} className="bg-primary text-white font-label-caps text-xs tracking-widest px-10 py-4 rounded-full transition-all uppercase btn-luxury" style={{backgroundColor: inv.accent_color}}>
                      {submitting ? "Envoi..." : "Confirmer mon absence"}
                    </button>
                  ) : (
                    <button onClick={() => setCurrentStep(3)} className="bg-primary text-white font-label-caps text-xs tracking-widest px-10 py-4 rounded-full transition-all uppercase btn-luxury" style={{backgroundColor: inv.accent_color}}>
                      Suivant
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="w-full max-w-2xl mx-auto flex flex-col items-center animate-fade-in-up">
              <div className="step-progress-dots mb-8">
                <div className="step-dot active"></div>
                <div className="step-dot active"></div>
                <div className="step-dot active" style={{backgroundColor: inv.accent_color}}></div>
                <div className="step-dot"></div>
              </div>
              <div className="text-center mb-12">
                <span className="font-label-caps text-[11px] uppercase tracking-[0.5em] mb-4 block" style={{color: inv.accent_color}}>Étape 3 sur 4</span>
                <h2 className="font-serif text-4xl text-on-surface mb-2">Membres de votre famille</h2>
                <p className="font-body-md text-on-surface-variant">Confirmez la présence des personnes qui vous accompagnent.</p>
              </div>
              <div className="w-full space-y-6 mb-12">
                {form.household_members?.map((m, idx) => (
                  <div key={m.guest_id} className="guest-row flex flex-col md:flex-row items-center justify-between p-6 border border-outline-variant/30 rounded-lg bg-white/50 backdrop-blur-sm gap-4">
                    <span className="font-body-lg text-on-surface font-medium">{m.name}</span>
                    <div className="flex items-center gap-6">
                      <div className={`segmented-control flex p-1 rounded-lg overflow-hidden h-[40px] items-center ${m.status === 'declined' ? 'absent' : ''}`}>
                        <div className="segmented-bg" style={{backgroundColor: inv.accent_color}}></div>
                        <button onClick={() => {
                          const newHm = [...form.household_members];
                          newHm[idx].status = 'confirmed';
                          setForm({...form, household_members: newHm});
                        }} className={`segmented-btn font-label-caps text-[11px] px-6 h-full flex items-center rounded-md ${m.status === 'confirmed' ? 'active' : 'text-on-surface-variant'}`}>PRÉSENT</button>
                        <button onClick={() => {
                          const newHm = [...form.household_members];
                          newHm[idx].status = 'declined';
                          setForm({...form, household_members: newHm});
                        }} className={`segmented-btn font-label-caps text-[11px] px-6 h-full flex items-center rounded-md ${m.status === 'declined' ? 'active' : 'text-on-surface-variant'}`}>ABSENT</button>
                      </div>
                    </div>
                  </div>
                ))}

                {guest.plus_ones_count > 0 && Array.from({ length: guest.plus_ones_count }).map((_, i) => {
                  const companion = form.plus_one_responses?.[i] || { name: form.plus_one_names?.[i] || "", status: "pending" };
                  const isRegisteredCompanion = Boolean(companion.name?.trim());
                  return (
                  <div key={`plusone-${i}`} className="guest-row flex flex-col md:flex-row items-center justify-between p-6 border border-outline-variant/30 rounded-lg bg-white/50 backdrop-blur-sm gap-4">
                    <input 
                      type="text"
                      placeholder="Accompagnant à confirmer avec les mariés"
                      value={companion.name || ""}
                      disabled
                      className="bg-transparent border-0 border-b border-outline-variant font-body-lg text-on-surface w-full md:w-auto min-w-[200px] placeholder:text-outline-variant transition-colors disabled:opacity-70"
                      style={{borderColor: inv.accent_color}}
                    />
                    <div className="flex items-center gap-3">
                      <span className="font-label-caps text-[11px] text-on-surface-variant tracking-widest">ACCOMPAGNANT</span>
                      <button type="button" onClick={() => {
                        if (!isRegisteredCompanion) return;
                        const responses = [...(form.plus_one_responses || [])];
                        responses[i] = { ...companion, status: "confirmed" };
                        setForm({ ...form, plus_one_responses: responses });
                      }} disabled={!isRegisteredCompanion} className={`text-[10px] px-3 py-2 rounded disabled:cursor-not-allowed disabled:opacity-40 ${companion.status === "confirmed" ? "bg-success text-white" : "bg-success/10 text-success"}`}>OUI</button>
                      <button type="button" onClick={() => {
                        if (!isRegisteredCompanion) return;
                        const responses = [...(form.plus_one_responses || [])];
                        responses[i] = { ...companion, status: "declined" };
                        setForm({ ...form, plus_one_responses: responses });
                      }} disabled={!isRegisteredCompanion} className={`text-[10px] px-3 py-2 rounded disabled:cursor-not-allowed disabled:opacity-40 ${companion.status === "declined" ? "bg-danger text-white" : "bg-danger/10 text-danger"}`}>NON</button>
                    </div>
                  </div>
                )})}
                
                {(!form.household_members?.length && guest.plus_ones_count === 0) && (
                  <div className="text-center p-8 text-on-surface-variant italic opacity-70">
                    Cette invitation est individuelle.
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between w-full pt-8 border-t border-outline-variant/30">
                <button onClick={() => setCurrentStep(2)} className="flex items-center gap-2 font-label-caps text-xs tracking-widest text-on-surface-variant hover:text-primary uppercase transition-all btn-text-luxury">
                  <span className="material-symbols-outlined text-sm">west</span> Retour
                </button>
                <button onClick={() => setCurrentStep(4)} className="bg-primary text-white font-label-caps text-xs tracking-widest px-10 py-4 rounded-full transition-all uppercase btn-luxury shadow-lg" style={{backgroundColor: inv.accent_color}}>
                  Suivant
                </button>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="w-full max-w-2xl mx-auto flex flex-col items-center animate-fade-in-up">
              <div className="flex items-center space-x-3 mb-12">
                <span className="w-2 h-2 rounded-full opacity-20" style={{backgroundColor: inv.accent_color}}></span>
                <span className="w-2 h-2 rounded-full opacity-20" style={{backgroundColor: inv.accent_color}}></span>
                <span className="w-2 h-2 rounded-full opacity-20" style={{backgroundColor: inv.accent_color}}></span>
                <div className="flex flex-col items-center">
                  <span className="w-12 h-2 rounded-full transition-all duration-500 mb-2" style={{backgroundColor: inv.accent_color}}></span>
                  <span className="font-label-caps text-[10px] uppercase tracking-widest" style={{color: inv.accent_color}}>Étape 4 de 4</span>
                </div>
              </div>
              
              <div className="w-full bg-white/90 backdrop-blur-sm rounded-xl p-8 md:p-12 editorial-shadow text-left border border-white/50 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full -mr-12 -mt-12 opacity-10" style={{backgroundColor: inv.accent_color}}></div>
                <h2 className="font-serif text-3xl text-on-surface mb-10 text-center">Détails Finaux</h2>
                
                <div className="space-y-12">
                  {form.status !== "declined" && (
                  <div className="group relative">
                    <label className="font-label-caps text-[11px] text-on-surface-variant uppercase tracking-widest block mb-4 transition-colors">
                      Restrictions Alimentaires (Optionnel)
                    </label>
                    <textarea 
                      value={form.dietary || ""}
                      onChange={(e) => setForm({ ...form, dietary: e.target.value })}
                      className="w-full input-underline py-4 font-body-md text-on-surface resize-none placeholder:text-outline-variant/60" 
                      placeholder="Allergies, régimes spécifiques..." 
                      rows="2"
                      style={{borderBottomColor: inv.accent_color}}
                    ></textarea>
                  </div>
                  )}
                  <div className="group relative">
                    <label className="font-label-caps text-[11px] text-on-surface-variant uppercase tracking-widest block mb-4 transition-colors">
                      Un mot pour les mariés ?
                    </label>
                    <textarea 
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full input-underline py-4 font-body-md text-on-surface resize-none placeholder:text-outline-variant/60" 
                      placeholder="Félicitations, hâte d'y être..." 
                      rows="2"
                      style={{borderBottomColor: inv.accent_color}}
                    ></textarea>
                  </div>
                  
                  <div className="flex flex-col md:flex-row items-center justify-between pt-8 space-y-6 md:space-y-0">
                    <button type="button" onClick={() => setCurrentStep(form.status === "declined" ? 1 : 3)} className="flex items-center font-label-caps text-[11px] text-on-surface-variant hover:text-primary transition-colors group uppercase tracking-widest btn-text-luxury">
                      <span className="material-symbols-outlined mr-2">arrow_back</span> Retour
                    </button>
                    <button 
                      onClick={submit}
                      disabled={submitting}
                      className="shimmer-btn text-white font-label-caps text-[11px] uppercase tracking-widest px-10 py-4 rounded-lg shadow-md active:scale-95 transition-all duration-300 w-full md:w-auto text-center"
                      style={{backgroundColor: inv.accent_color}}
                    >
                      {submitting ? "Envoi..." : "Confirmer ma présence"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>      )}

      {view === "gifts" && (
        <div className="max-w-4xl mx-auto p-8 lg:p-16 cinematic-fade" key="gifts" data-testid="public-gifts">
          <div className="overline mb-2" style={{ color: inv.accent_color }}>Pour les mariés</div>
          <div className="font-serif text-4xl mb-8">Liste de cadeaux</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {gifts.map((g) => (
              <div key={g.id} className="border p-4 flex gap-4" style={{ borderColor: inv.accent_color + "30" }}>
                {g.image && <img src={g.image} alt="" className="w-20 h-20 object-cover" />}
                <div className="flex-1">
                  <div className="font-serif text-xl">{g.name}</div>
                  {g.type === "cash" ? (
                    g.show_amounts === false ? <div className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-white/20"><div className="h-full rounded-full bg-current" style={{ width: `${g.progress_percent || 0}%` }} /></div> : <div className="text-sm opacity-70 mt-1">{g.collected_amount}€ / {g.target_amount}€</div>
                  ) : <div className="text-sm opacity-70 mt-1">{g.price}€</div>}
                  {g.claimed_by ? (
                    <div className="text-xs mt-2 opacity-60">Réservé par {g.claimed_by}</div>
                  ) : (
                    <button onClick={() => claimGift(g.id, g.type)} className="mt-2 text-xs px-3 py-1.5" style={{ background: inv.accent_color, color: inv.background_color }} data-testid={`claim-${g.id}`}>{g.type === "cash" ? "Contribuer" : "Réserver"}</button>
                  )}
                </div>
              </div>
            ))}
            {gifts.length === 0 && <div className="opacity-60">Pas de liste pour le moment</div>}
          </div>
        </div>
      )}

      {view === "success_declined" && (
        <div className="max-w-2xl mx-auto p-8 lg:p-16 text-center cinematic-fade" key="success_declined">
          <div className="font-serif text-5xl lg:text-7xl leading-tight mb-8">Merci.</div>
          <div className="my-8 mx-auto w-16 h-px" style={{ background: inv.accent_color }} />
          <p className="text-base lg:text-xl leading-relaxed whitespace-pre-wrap font-light">
            Nous avons bien pris note de votre absence et nous la regrettons sincèrement.<br/><br/>
            Nous penserons fort à vous lors de cette belle journée !
          </p>
          <div className="mt-16">
            <span className="material-symbols-outlined text-[48px] opacity-50" style={{color: inv.accent_color}}>favorite</span>
          </div>
        </div>
      )}

      {/* Floating Chat Widget */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
        {chatOpen && (
          <div className="mb-4 w-80 sm:w-96 bg-white shadow-2xl border flex flex-col overflow-hidden reveal" style={{ borderColor: inv.accent_color + "40", borderRadius: '24px' }}>
            <div className="p-4 flex items-center justify-between text-white" style={{ background: inv.accent_color }}>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                <span className="font-serif text-lg">Message aux mariés</span>
              </div>
              <button onClick={() => setChatOpen(false)} className="opacity-80 hover:opacity-100 transition-opacity">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 p-4 overflow-y-auto min-h-[300px] max-h-[400px] space-y-4 bg-[#FAF9F6]">
              {chatMessages.length === 0 ? (
                <div className="text-center text-sm font-light italic mt-10" style={{ color: inv.text_color + "80" }}>
                  Envoyez un message direct aux mariés...
                </div>
              ) : (
                chatMessages.map(m => {
                  const isGuest = m.sender === "guest";
                  return (
                    <div key={m.id} className={`flex flex-col ${isGuest ? "items-end" : "items-start"} reveal`}>
                      <div className={`p-3 rounded-2xl text-sm font-light max-w-[85%] ${
                        isGuest ? "text-white rounded-tr-none" : "bg-white border rounded-tl-none"
                      }`} style={{
                        background: isGuest ? inv.accent_color : "white",
                        color: isGuest ? inv.background_color : inv.text_color,
                        borderColor: isGuest ? "transparent" : inv.accent_color + "30"
                      }}>
                        {m.content}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendChat} className="p-3 border-t flex gap-2" style={{ borderColor: inv.accent_color + "20", background: "white" }}>
              <input
                type="text"
                placeholder="Votre message..."
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                className="flex-1 bg-[#FAF9F6] rounded-full px-4 py-2 text-sm focus:outline-none border"
                style={{ color: inv.text_color, borderColor: inv.accent_color + "40" }}
              />
              <button 
                type="submit" 
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 hover:scale-105 active:scale-95 transition-all text-white shadow-md"
                style={{ background: inv.accent_color }}
              >
                <Send className="w-4 h-4 ml-0.5" />
              </button>
            </form>
          </div>
        )}

        <button 
          onClick={() => setChatOpen(!chatOpen)}
          className="w-14 h-14 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 active:scale-95 transition-all relative"
          style={{ background: inv.accent_color, color: inv.background_color }}
        >
          {chatOpen ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
          {!chatOpen && chatMessages.some(m => m.sender === "admin" && !m.read) && (
            <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 border-2 border-white rounded-full"></span>
          )}
        </button>
      </div>
    </div>
  );
}
